import { LEGAL_PATHS } from "./legalPaths";

/**
 * Card + document registry for Legal Center and document shells.
 * DEVELOPER NOTE: All linked policies are starter templates — attorney review is required before public launch.
 */
export const LEGAL_LAST_UPDATED = "May 17, 2026";

export const LEGAL_CARD_GROUPS = [
  {
    id: "platform",
    title: "Platform Rules",
    subtitle: "How you may use Treydora services, accounts, and software access.",
  },
  {
    id: "digital",
    title: "Digital Asset & NFT Terms",
    subtitle: "On-chain collectibles, reader access, licensing boundaries, and risk disclosures.",
  },
  {
    id: "marketplace",
    title: "Marketplace / Storefront Terms",
    subtitle: "Seller participation, listings, and platform transaction fees.",
  },
  {
    id: "protection",
    title: "Privacy, Refunds & User Protection",
    subtitle: "Data practices, refund windows, and community safety standards.",
  },
];

export const LEGAL_DOCUMENTS = [
  {
    slug: "terms",
    path: LEGAL_PATHS.terms,
    title: "Terms of Use",
    shortDescription: "Master platform agreement covering accounts, services, acceptable use, and limitations.",
    category: "Platform",
    categoryTone: "platform",
    groupId: "platform",
    summary:
      "These Terms govern your access to Treydora websites, dashboards, readers, checkout flows, and related digital services.",
    showWarning: false,
  },
  {
    slug: "privacy",
    path: LEGAL_PATHS.privacy,
    title: "Privacy Policy",
    shortDescription: "How Treydora collects, uses, stores, and shares personal information tied to your account.",
    category: "Privacy",
    categoryTone: "privacy",
    groupId: "protection",
    summary:
      "Describes categories of data we may process for authentication, purchases, support, analytics, and legal compliance.",
    showWarning: false,
  },
  {
    slug: "digital-asset-risk",
    path: LEGAL_PATHS.digitalAssetRisk,
    title: "Digital Asset Risk Disclosure",
    shortDescription: "Volatility, technology, custody, and operational risks common to blockchain-based products.",
    category: "Risk Disclosure",
    categoryTone: "risk",
    groupId: "digital",
    summary:
      "Plain-language summary of risks including wallet compromise, chain reorganizations, smart contract bugs, gas volatility, and irreversible transactions.",
    showWarning: true,
    warningTitle: "Not investment, legal, or tax advice",
    warningBody:
      "This disclosure describes general categories of risk. It does not recommend buying, selling, or holding any digital asset, and is not individualized advice.",
  },
  {
    slug: "nft-comic-merch-license",
    path: LEGAL_PATHS.nftComicMerchLicense,
    title: "NFT Comic Merchandiser License Terms",
    shortDescription: "Limited, conditional merchandising rights tied to valid NFT ownership; Treydora retains underlying IP.",
    category: "NFT Rights",
    categoryTone: "nft",
    groupId: "digital",
    summary:
      "Qualifies how eligible Genesis Comic NFT tiers may reference Treydora IP for approved merchandise, subject to revocation on transfer loss or breach.",
    showWarning: true,
    warningTitle: "IP ownership stays with Treydora",
    warningBody:
      "Treydora Productions, Inc. and its licensors retain ownership of underlying comic IP. Eligible holders receive a limited license, not an assignment of copyright or trademark.",
  },
  {
    slug: "vr-real-estate-terms",
    path: LEGAL_PATHS.vrRealEstateTerms,
    title: "VR Real Estate NFT Terms",
    shortDescription: "Virtual storefront and digital location licenses inside the Treydora / Oshar ecosystem only.",
    category: "NFT Rights",
    categoryTone: "nft",
    groupId: "digital",
    summary:
      "Defines virtual real estate as platform utility and digital access — not real-world land, deeds, equity, securities, or guaranteed economic returns.",
    showWarning: true,
    warningTitle: "Virtual access only",
    warningBody:
      "Purchases may grant digital licenses, cosmetic status, or storefront access inside Treydora experiences. They do not convey title to physical property or passive income rights.",
  },
  {
    slug: "marketplace-seller-terms",
    path: LEGAL_PATHS.marketplaceSellerTerms,
    title: "Marketplace Seller Terms",
    shortDescription: "Rules for storefront sellers, prohibited goods, payouts, chargebacks, and platform transaction fees.",
    category: "Marketplace",
    categoryTone: "marketplace",
    groupId: "marketplace",
    summary:
      "Sets expectations for seller conduct, listing accuracy, fulfillment, refunds, and Treydora’s right to suspend listings that violate law or platform standards.",
    showWarning: true,
    warningTitle: "Platform transaction fees",
    warningBody:
      "Treydora may retain a platform transaction fee on qualifying sales. Fees are not taxes unless separately stated by law and collected as such.",
  },
  {
    slug: "refund-policy",
    path: LEGAL_PATHS.refundPolicy,
    title: "Refund Policy",
    shortDescription: "Eligibility, timelines, and process for digital goods, failed deliveries, and duplicate charges.",
    category: "Refunds",
    categoryTone: "refunds",
    groupId: "protection",
    summary:
      "Explains when refunds or credits may be available for Stripe purchases, and when digital delivery is deemed complete.",
    showWarning: false,
  },
  {
    slug: "content-standards",
    path: LEGAL_PATHS.contentStandards,
    title: "Content Standards",
    shortDescription: "Community-facing rules for harassment, illegal content, impersonation, and IP respect.",
    category: "User Protection",
    categoryTone: "protection",
    groupId: "protection",
    summary:
      "Baseline standards applied to user-generated material, marketplace listings, support interactions, and public areas of Treydora services.",
    showWarning: false,
  },
];

export function getLegalDocument(slug) {
  const s = String(slug || "").trim().toLowerCase();
  return LEGAL_DOCUMENTS.find((d) => d.slug === s) || null;
}

export function legalDocumentsInGroup(groupId) {
  return LEGAL_DOCUMENTS.filter((d) => d.groupId === groupId);
}
